<template>
  <div class="list-pagination">
    <div class="list-pagination__acts">
      <button
        class="btn btn--ghost btn--sm"
        :disabled="page <= 1"
        aria-label="Página anterior"
        @click="goTo(page - 1)"
      >
        ‹ Anterior
      </button>
      <template v-for="(item, index) in pageItems">
        <span
          v-if="item === ELLIPSIS"
          :key="`ellipsis-${index}`"
          class="list-pagination__page list-pagination__ellipsis"
        >
          {{ item }}
        </span>
        <button
          v-else
          :key="item"
          class="list-pagination__page btn btn--sm"
          :class="item === page ? 'btn--primary' : 'btn--ghost'"
          :aria-current="item === page ? 'page' : null"
          :aria-label="`Página ${item}`"
          @click="item !== page && goTo(item)"
        >
          {{ item }}
        </button>
      </template>
      <button
        class="btn btn--outline btn--sm"
        :disabled="page >= totalPages"
        aria-label="Próxima página"
        @click="goTo(page + 1)"
      >
        Próxima ›
      </button>
    </div>
  </div>
</template>

<script>
// Paginação de listas paginadas no back: a tela informa a página atual e o
// total (Total-Count / total_count) e busca a página emitida em `change`.
const ELLIPSIS = "…";
// Até esse número de páginas mostra todas; acima, só primeira, última e as
// vizinhas da atual, com reticências entre elas.
const MAX_VISIBLE_PAGES = 7;

export default {
  name: "ListPagination",
  props: {
    page: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    // Mesmo tamanho de página dos endpoints paginados do back.
    perPage: {
      type: Number,
      default: 10
    },
    label: {
      type: String,
      default: "itens"
    }
  },
  data() {
    return { ELLIPSIS };
  },
  methods: {
    goTo(page) {
      this.$emit("change", page);
      this.scrollToTop();
    },
    // Página nova começa do topo da tela.
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  },
  computed: {
    pageItems() {
      const last = this.totalPages;
      if (last <= MAX_VISIBLE_PAGES) {
        return Array.from({ length: last }, (_, i) => i + 1);
      }

      const from = Math.max(2, this.page - 1);
      const to = Math.min(last - 1, this.page + 1);
      const items = [1];
      if (from > 2) items.push(ELLIPSIS);
      for (let page = from; page <= to; page++) items.push(page);
      if (to < last - 1) items.push(ELLIPSIS);
      items.push(last);
      return items;
    },
    totalPages() {
      return Math.ceil(this.total / this.perPage);
    },
    start() {
      return this.total ? (this.page - 1) * this.perPage + 1 : 0;
    },
    end() {
      return Math.min(this.page * this.perPage, this.total);
    }
  }
};
</script>

<style lang="scss" scoped>
.list-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4) 0;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.list-pagination__info {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}


.list-pagination__acts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.list-pagination__page {
  min-width: 32px;
  justify-content: center;
}

.list-pagination__ellipsis {
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  text-align: center;
}
</style>
